import React from 'react';

// Shared client-side search + filter + sort + pagination for admin list tables.
// Nothing here talks to the network; it only narrows, reorders and slices rows
// the caller already has in memory.

interface ListControlsOptions<T> {
  searchText?: (row: T) => string;
  sorters?: Record<string, (row: T) => string | number>;
  filters?: Record<string, (row: T, value: string) => boolean>;
  defaultPageSize?: number;
}

export function useListControls<T>(rows: T[], { searchText, sorters = {}, filters = {}, defaultPageSize = 10 }: ListControlsOptions<T> = {}) {
  const [query, setQuery] = React.useState('');
  const [sortKey, setSortKey] = React.useState<string | null>(null);
  const [sortDir, setSortDir] = React.useState<'asc' | 'desc'>('asc');
  const [filterValues, setFilterValues] = React.useState<Record<string, string>>({});
  const [pageSize, setPageSizeState] = React.useState<number | 'all'>(defaultPageSize);
  const [page, setPageState] = React.useState(1);

  const onQuery = React.useCallback((v: string) => { setQuery(v); setPageState(1); }, []);
  const setFilter = React.useCallback((key: string, value: string) => {
    setFilterValues((f) => ({ ...f, [key]: value }));
    setPageState(1);
  }, []);
  const clearFilters = React.useCallback(() => { setFilterValues({}); setQuery(''); setPageState(1); }, []);
  const toggleSort = React.useCallback((key: string) => {
    setSortKey((prevKey) => {
      setSortDir((prevDir) => (prevKey === key ? (prevDir === 'asc' ? 'desc' : 'asc') : 'asc'));
      return key;
    });
  }, []);
  const setPageSize = React.useCallback((n: number | 'all') => { setPageSizeState(n); setPageState(1); }, []);

  const filtered = React.useMemo(() => {
    let out = rows;
    const q = query.trim().toLowerCase();
    if (q && searchText) out = out.filter((r) => searchText(r).toLowerCase().includes(q));
    Object.keys(filterValues).forEach((k) => {
      const v = filterValues[k];
      const test = filters[k];
      if (v == null || v === '' || !test) return;
      out = out.filter((r) => test(r, v));
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, query, filterValues, searchText, filters]);

  const sorted = React.useMemo(() => {
    const get = sortKey && sorters[sortKey];
    if (!get) return filtered;
    const withIdx = filtered.map((row, i) => ({ row, i, v: get(row) }));
    withIdx.sort((a, b) => {
      if (a.v == null && b.v == null) return a.i - b.i;
      if (a.v == null) return 1;
      if (b.v == null) return -1;
      let cmp: number;
      if (typeof a.v === 'number' && typeof b.v === 'number') cmp = a.v - b.v;
      else cmp = String(a.v).localeCompare(String(b.v), undefined, { sensitivity: 'base', numeric: true });
      return cmp !== 0 ? cmp : a.i - b.i;
    });
    if (sortDir === 'desc') withIdx.reverse();
    return withIdx.map((x) => x.row);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered, sortKey, sortDir, sorters]);

  const filteredCount = sorted.length;
  const totalPages = pageSize === 'all' ? 1 : Math.max(1, Math.ceil(filteredCount / pageSize));
  const page1 = Math.min(Math.max(1, page), totalPages);

  const pageRows = React.useMemo(() => {
    if (pageSize === 'all') return sorted;
    const start = (page1 - 1) * (pageSize as number);
    return sorted.slice(start, start + (pageSize as number));
  }, [sorted, page1, pageSize]);

  return {
    query, onQuery,
    sortKey, sortDir, toggleSort,
    filterValues, setFilter, clearFilters,
    hasActiveFilters: !!query.trim() || Object.values(filterValues).some((v) => v != null && v !== ''),
    page: page1, setPage: setPageState, pageSize, setPageSize,
    pageRows, totalCount: rows.length, filteredCount, totalPages,
  };
}

interface SortThProps {
  label: string;
  sortKey?: string;
  controls: ReturnType<typeof useListControls>;
  className?: string;
  num?: boolean;
}

export function SortTh({ label, sortKey, controls, className, num }: SortThProps) {
  if (!sortKey) return <th scope="col" className={className}>{label}</th>;
  const active = controls.sortKey === sortKey;
  const dir = active ? controls.sortDir : null;
  return (
    <th scope="col" className={num ? 'th--num' : className} aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <button
        type="button"
        className={num ? 'th th--num' : 'th'}
        onClick={() => controls.toggleSort(sortKey)}
      >
        {label}
      </button>
    </th>
  );
}

interface PagerProps {
  controls: ReturnType<typeof useListControls>;
  itemLabel?: string;
}

export function Pager({ controls, itemLabel = 'items' }: PagerProps) {
  if (controls.totalPages <= 1) return null;
  const size = controls.pageSize === 'all' ? controls.filteredCount : (controls.pageSize as number);
  const start = controls.filteredCount === 0 ? 0 : (controls.page - 1) * size + 1;
  const end = Math.min(controls.page * size, controls.filteredCount);
  return (
    <div className="stg-pager">
      <span className="stg-fig">
        {controls.filteredCount === 0 ? `0 ${itemLabel}` : `${start}–${end} of ${controls.filteredCount} ${itemLabel}`}
      </span>
      <div className="stg-pager-nav">
        <button type="button" className="btn btn--sm" disabled={controls.page <= 1} onClick={() => controls.setPage(controls.page - 1)}>Prev</button>
        <span className="stg-fig">Page {controls.page} of {controls.totalPages}</span>
        <button type="button" className="btn btn--sm" disabled={controls.page >= controls.totalPages} onClick={() => controls.setPage(controls.page + 1)}>Next</button>
      </div>
    </div>
  );
}

export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
